// Chemin: C:\gestion-copro\src\services\courrier.service.ts
// Service de gestion des courriers sortants - Copro Protect
// Session 40 : création initiale - module Communications
// Session 43 : ajout analyserCourrierSCUD pour analyse SCUD avant soumission
// Session 52 : support SENTINELLE V2 (nature, motsClesEngageants, reformulationProposee)
// Session 80 : ajout coproprieteId pour gestion quota SENTINELLE
// Session 81 : correction fonctions vérification votes (aUnanimiteLocal, estRejeteLocal)
// Session 92 : Classification 3 niveaux (VERT/ORANGE/ROUGE) - Gouvernance V2
// Session 114 : Fix VIGIE "en_validation" → "valide" — VIGIE n'a pas de CS pour voter

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import {
  Courrier,
  CourrierCreateData,
  CourrierUpdateData,
  VoteData,
  StatutCourrier,
  VoteCourrier,
  AnalyseSCUD,
  AlerteSCUD,
  NiveauRisque,
  NatureCommunication,
  NiveauClassification,
  ReformulationProposee,
  determinerNiveauClassification,
  necessiteVoteCS,
  peutSurclasser,
  NIVEAU_CLASSIFICATION_ORDRE
} from '../types/courrier.types';
import { createLog } from './log.service';

const COLLECTION_NAME = 'courriers';

// ============================================================================
// TYPES POUR L'ANALYSE SCUD - ENRICHIS SENTINELLE V2 + CLASSIFICATION V2
// ============================================================================

// Session 80 : Ajout coproprieteId pour quota
// Session 300 : Ajout profilUtilisateur pour analyse profil-aware
interface AnalyseCourrierRequest {
  coproprieteId: string;
  objet: string;
  contenu: string;
  destinataire: string;
  typeCourrier: string;
  profilUtilisateur?: 'vigie' | 'cs';
}

// Session 92 : Ajout niveauClassification dans la réponse
// Session 126 : Fix undefined dans details.niveauClassification (finalisation directe sans SCUD)
interface AnalyseCourrierResponse {
  scoreRisque: NiveauRisque;
  /** Nature de la communication - SENTINELLE V2 */
  nature?: NatureCommunication;
  /** Classification 3 niveaux - Gouvernance V2 (Session 92) */
  niveauClassification?: NiveauClassification;
  alertes: AlerteSCUD[];
  recommandations: string[];
  analyseComplete: string;
  /** Mots-clés engageants détectés - SENTINELLE V2 */
  motsClesEngageants?: string[];
  /** Reformulation proposée si risque élevé - SENTINELLE V2 */
  reformulationProposee?: {
    objet: string;
    contenu: string;
  };
}

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

/**
 * Génère le prochain numéro de courrier
 * Format: COU-2026-001
 */
async function generateNumeroCourrier(coproprieteId: string): Promise<string> {
  const year = new Date().getFullYear();
  const prefix = `COU-${year}-`;
  
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('numero', '>=', prefix),
    where('numero', '<', `COU-${year + 1}-`)
  );
  
  const snapshot = await getDocs(q);
  const count = snapshot.size;
  const nextNumber = (count + 1).toString().padStart(3, '0');
  
  return `${prefix}${nextNumber}`;
}

/**
 * Supprime les propriétés undefined d'un objet
 */
function removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Récupère l'adresse IP du client (côté client, limité)
 * En production, utiliser une Cloud Function pour obtenir l'IP réelle
 */
async function getClientIp(): Promise<string | undefined> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return undefined;
  }
}

/**
 * Session 81 : Fonction locale pour vérifier l'unanimité des votes
 * Utilisée pendant le processus de vote avant mise à jour du courrier
 */
function checkUnanimite(
  validations: { [membreId: string]: VoteCourrier },
  votesRequis: number
): boolean {
  const votes = Object.values(validations);
  if (votes.length < votesRequis) return false;
  return votes.every(v => v.statut === 'approuve');
}

/**
 * Session 92 : Vérifie si au moins 1 vote d'approbation existe
 * Pour les courriers ROUGE : 1 seule approbation suffit (Gouvernance V2)
 */
function checkApprobationMinimale(
  validations: { [membreId: string]: VoteCourrier }
): boolean {
  const votes = Object.values(validations);
  return votes.some(v => v.statut === 'approuve');
}

/**
 * Session 81 : Fonction locale pour vérifier si un vote de rejet existe
 * Utilisée pendant le processus de vote avant mise à jour du courrier
 */
function checkRejete(validations: { [membreId: string]: VoteCourrier }): boolean {
  const votes = Object.values(validations);
  return votes.some(v => v.statut === 'rejete');
}

// ============================================================================
// ANALYSE SCUD (AVANT SOUMISSION) - ENRICHI SENTINELLE V2 + CLASSIFICATION
// ============================================================================

/**
 * Analyse un courrier avec la méthode SCUD avant soumission
 * Retourne les alertes, recommandations, nature et niveau de classification
 * 
 * Session 52 : Enrichi pour retourner nature, motsClesEngageants et reformulationProposee
 * Session 80 : Ajout coproprieteId obligatoire pour gestion quota SENTINELLE
 * Session 92 : Ajout niveauClassification dans le retour
 */
export async function analyserCourrierSCUD(
  coproprieteId: string,
  objet: string,
  contenu: string,
  destinataire: string,
  typeCourrier: string,
  profilUtilisateur?: 'vigie' | 'cs'
): Promise<AnalyseSCUD> {
  const analyzeFunction = httpsCallable<AnalyseCourrierRequest, AnalyseCourrierResponse>(
    functions,
    'analyzeCourrierSCUD'
  );

  try {
    const result = await analyzeFunction({
      coproprieteId,
      objet,
      contenu,
      destinataire,
      typeCourrier,
      profilUtilisateur
    });

    // Session 92 : Calculer le niveauClassification si absent de la réponse
    const niveauClassification = result.data.niveauClassification 
      || determinerNiveauClassification(
          result.data.scoreRisque,
          result.data.nature,
          result.data.motsClesEngageants
        );

    // Construire l'objet AnalyseSCUD enrichi avec les champs V2 + classification
    const analyse: AnalyseSCUD = {
      dateAnalyse: Timestamp.now(),
      scoreRisque: result.data.scoreRisque,
      nature: result.data.nature,
      niveauClassification,
      alertes: result.data.alertes,
      recommandations: result.data.recommandations,
      analyseComplete: result.data.analyseComplete,
      motsClesEngageants: result.data.motsClesEngageants,
    };

    // Ajouter la reformulation si présente
    if (result.data.reformulationProposee) {
      analyse.reformulationProposee = {
        objet: result.data.reformulationProposee.objet,
        contenu: result.data.reformulationProposee.contenu,
      };
    }

    return analyse;
  } catch (error) {
    console.error('Erreur analyse SCUD:', error);
    
    // En cas d'erreur, retourner une analyse neutre avec niveau ORANGE par prudence
    return {
      dateAnalyse: Timestamp.now(),
      scoreRisque: 'moyen',
      nature: 'courante',
      niveauClassification: 'orange',
      alertes: [{
        type: 'terme_risque',
        message: 'L\'analyse automatique n\'a pas pu être effectuée. Veuillez relire attentivement votre courrier.',
        suggestion: 'Vérifiez manuellement le ton et le contenu avant de soumettre.'
      }],
      recommandations: ['Relecture manuelle recommandée avant soumission'],
      analyseComplete: 'Analyse automatique indisponible. Relecture manuelle conseillée.'
    };
  }
}

// ============================================================================
// CRUD DE BASE
// ============================================================================

/**
 * Crée un nouveau courrier (statut: brouillon)
 * Session 52 : Support du champ analyseSentinelle pré-rempli
 * Session 92 : Support niveauClassification
 */
export async function createCourrier(
  data: CourrierCreateData,
  votesRequis: number
): Promise<string> {
  const now = Timestamp.now();
  const numero = await generateNumeroCourrier(data.coproprieteId);
  
  // Construire l'objet courrier
  const courrierData: Record<string, unknown> = {
    coproprieteId: data.coproprieteId,
    numero,
    objet: data.objet,
    contenu: data.contenu,
    type: data.type,
    modeEnvoi: data.modeEnvoi,
    destinataire: data.destinataire,
    piecesJointes: (data.piecesJointes || []).map(pj => ({
      ...pj,
      id: crypto.randomUUID()
    })),
    statut: 'brouillon',
    version: 1,
    redacteurId: data.redacteurId,
    redacteurNom: data.redacteurNom,
    dateCreation: now,
    dateModification: now,
    validations: {},
    votesRequis,
    createdAt: now,
    updatedAt: now
  };
  
  // Ajouter les champs optionnels seulement s'ils ont une valeur
  if (data.affaireId) {
    courrierData.affaireId = data.affaireId;
  }
  if (data.signalementIds && data.signalementIds.length > 0) {
    courrierData.signalementIds = data.signalementIds;
  }
  if (data.courrierReponseA) {
    courrierData.courrierReponseA = data.courrierReponseA;
  }
  
  // SENTINELLE V2 : Ajouter l'analyse pré-remplie si fournie
  if (data.analyseSentinelle) {
    // Session 92 : Inclure niveauClassification dans l'analyse pré-remplie
    const niveauClassification = data.analyseSentinelle.niveauClassification
      || determinerNiveauClassification(
          data.analyseSentinelle.risque,
          data.analyseSentinelle.nature
        );

    courrierData.analyseSentinelle = {
      dateAnalyse: now,
      scoreRisque: data.analyseSentinelle.risque,
      nature: data.analyseSentinelle.nature,
      niveauClassification,
      alertes: [],
      recommandations: [],
      analyseComplete: 'Analyse pré-remplie depuis la Messagerie'
    };

    // Stocker le niveau sur le courrier lui-même
    courrierData.niveauClassification = niveauClassification;
  }
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), courrierData);
  
  // Log de création
  await createLog({
    coproprieteId: data.coproprieteId,
    categorie: 'courrier',
    action: 'courrier_cree',
    acteurId: data.redacteurId,
    acteurNom: data.redacteurNom,
    cibleType: 'courrier',
    cibleId: docRef.id,
    cibleLibelle: data.objet,
    description: `Courrier "${data.objet}" créé`
  });
  
  return docRef.id;
}

/**
 * Récupère un courrier par son ID
 */
export async function getCourrierById(id: string): Promise<Courrier | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data()
  } as Courrier;
}

/**
 * Récupère tous les courriers d'une copropriété
 */
export async function getCourriersByCopropriete(coproprieteId: string): Promise<Courrier[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({
    id: d.id,
    ...d.data()
  })) as Courrier[];
}

/**
 * Met à jour un courrier (brouillon uniquement)
 */
export async function updateCourrier(
  id: string,
  data: CourrierUpdateData
): Promise<void> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  if (courrier.statut !== 'brouillon') {
    throw new Error('Seuls les brouillons peuvent être modifiés');
  }
  
  const now = Timestamp.now();
  
  const updateData = {
    ...removeUndefinedFields(data as unknown as Record<string, unknown>),
    dateModification: now,
    updatedAt: now
  };
  
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, updateData);
}

// ============================================================================
// WORKFLOW DE VALIDATION - GOUVERNANCE V2 (Session 92)
// ============================================================================

/**
 * Soumet un courrier selon son niveau de classification
 * 
 * Session 92 : Adaptation Gouvernance V2
 * - VERT / ORANGE → passe directement en 'valide' (coordinateur envoie seul)
 * - ROUGE → passe en 'en_validation' (vote CS requis, min. 1 approbation)
 * - Si pas de niveau défini → ancien comportement (en_validation avec unanimité)
 * 
 * Session 114 : Fix VIGIE — un copropriétaire solo n'a pas de CS pour voter.
 * Les courriers VIGIE passent TOUJOURS en 'valide' quel que soit le niveau.
 * Détection : coproprieteId commence par 'vigie-' OU votesRequis === 0
 */
export async function soumettreCourrierValidation(
  id: string,
  userId: string,
  userNom: string
): Promise<{ nouveauStatut: StatutCourrier; niveauClassification?: NiveauClassification }> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  if (courrier.statut !== 'brouillon') {
    throw new Error('Seuls les brouillons peuvent être soumis');
  }
  
  const now = Timestamp.now();
  const docRef = doc(db, COLLECTION_NAME, id);
  
  const niveau = courrier.niveauClassification 
    || courrier.analyseSentinelle?.niveauClassification;
  
  // Session 114 : VIGIE = copropriétaire solo → pas de CS → validation directe systématique
  // Un VIGIE est seul décideur, le workflow de vote CS ne s'applique pas.
  const isVigie = courrier.coproprieteId.startsWith('vigie-') || courrier.votesRequis === 0;
  
  // Session 92 : Workflow selon le niveau de classification
  // Session 114 : VIGIE → toujours validation directe
  if (isVigie || (niveau && !necessiteVoteCS(niveau))) {
    // ============================================
    // VIGIE (toujours) / VERT / ORANGE → Validation directe
    // Le coordinateur ou le VIGIE peut envoyer sans vote CS
    // ============================================
    await updateDoc(docRef, {
      statut: 'valide',
      dateSoumission: now,
      dateValidation: now,
      votesRequis: 0,
      updatedAt: now
    });
    
    const description = isVigie
      ? `Courrier "${courrier.objet}" validé directement (VIGIE — pas de vote CS)`
      : `Courrier "${courrier.objet}" validé directement (niveau ${niveau?.toUpperCase()})`;
    
    await createLog({
      coproprieteId: courrier.coproprieteId,
      categorie: 'courrier',
      action: 'courrier_valide',
      niveau: 'info',
      acteurId: userId,
      acteurNom: userNom,
      cibleType: 'courrier',
      cibleId: id,
      cibleLibelle: courrier.objet,
      description,
      details: removeUndefinedFields({ niveauClassification: niveau, isVigie })
    });
    
    return { nouveauStatut: 'valide', niveauClassification: niveau };
    
  } else {
    // ============================================
    // ROUGE (ou pas de niveau) → Vote CS requis
    // Minimum 1 membre CS doit approuver
    // Note : Ce bloc ne sera JAMAIS atteint pour un VIGIE
    // ============================================
    
    // Session 92 : Pour ROUGE, votesRequis = 1 (au lieu de l'unanimité)
    const votesRequis = niveau === 'rouge' ? 1 : courrier.votesRequis;
    
    await updateDoc(docRef, {
      statut: 'en_validation',
      dateSoumission: now,
      votesRequis,
      updatedAt: now
    });
    
    await createLog({
      coproprieteId: courrier.coproprieteId,
      categorie: 'courrier',
      action: 'courrier_soumis',
      niveau: 'important',
      acteurId: userId,
      acteurNom: userNom,
      cibleType: 'courrier',
      cibleId: id,
      cibleLibelle: courrier.objet,
      description: `Courrier "${courrier.objet}" soumis à validation CS (niveau ${niveau?.toUpperCase() || 'NON CLASSÉ'})`,
      details: { niveauClassification: niveau || 'non_classe', votesRequis }
    });
    
    return { nouveauStatut: 'en_validation', niveauClassification: niveau };
  }
}

/**
 * Enregistre le vote d'un membre CS sur un courrier
 * 
 * Session 92 : Pour les courriers ROUGE, 1 seule approbation suffit à valider
 * (remplace l'unanimité de l'ancien système)
 */
export async function voterCourrier(data: VoteData): Promise<void> {
  const courrier = await getCourrierById(data.courrierId);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  if (courrier.statut !== 'en_validation') {
    throw new Error('Ce courrier n\'est pas en cours de validation');
  }
  
  // Vérifier si le membre a déjà voté
  if (courrier.validations[data.membreId]) {
    throw new Error('Vous avez déjà voté pour ce courrier');
  }
  
  const now = Timestamp.now();
  const ip = await getClientIp();
  
  // Construire le vote
  const vote: VoteCourrier = {
    membreId: data.membreId,
    membreNom: data.membreNom,
    statut: data.statut,
    dateVote: now,
    commentaire: data.commentaire,
    ip,
    juridiquementValide: data.juridiquementValide
  };
  
  // Ajouter le vote aux validations existantes
  const nouvellesValidations = {
    ...courrier.validations,
    [data.membreId]: vote
  };
  
  // Déterminer le nouveau statut du courrier
  let nouveauStatut: StatutCourrier = courrier.statut;
  
  const niveau = courrier.niveauClassification 
    || courrier.analyseSentinelle?.niveauClassification;
  
  // Session 92 : Logique de validation adaptée selon le niveau
  if (niveau === 'rouge') {
    // ============================================
    // ROUGE : 1 seule approbation suffit à valider
    // 1 rejet suffit à rejeter
    // ============================================
    if (data.statut === 'approuve') {
      // 1 approbation → validé immédiatement
      nouveauStatut = 'valide';
    } else if (data.statut === 'rejete') {
      nouveauStatut = 'rejete';
    }
    // 'modification' → reste en_validation
    
  } else {
    // ============================================
    // Ancien système (compatibilité) : unanimité requise
    // Utilisé si pas de niveauClassification défini
    // ============================================
    const votes = Object.values(nouvellesValidations);
    const nbVotes = votes.length;
    
    if (nbVotes >= courrier.votesRequis) {
      if (checkUnanimite(nouvellesValidations, courrier.votesRequis)) {
        nouveauStatut = 'valide';
      } else if (checkRejete(nouvellesValidations)) {
        nouveauStatut = 'rejete';
      }
    }
  }
  
  // Mettre à jour le courrier
  const docRef = doc(db, COLLECTION_NAME, data.courrierId);
  const updateData: Record<string, unknown> = {
    validations: nouvellesValidations,
    updatedAt: now
  };
  
  if (nouveauStatut !== courrier.statut) {
    updateData.statut = nouveauStatut;
    if (nouveauStatut === 'valide') {
      updateData.dateValidation = now;
    }
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, updateData as any);
  
  // Log du vote
  const actionLog = data.statut === 'approuve' 
    ? 'courrier_vote_approuve' 
    : data.statut === 'modification' 
      ? 'courrier_vote_modification' 
      : 'courrier_vote_rejete';
  
  await createLog({
    coproprieteId: courrier.coproprieteId,
    categorie: 'courrier',
    action: actionLog,
    niveau: data.statut === 'rejete' ? 'important' : 'info',
    acteurId: data.membreId,
    acteurNom: data.membreNom,
    acteurIp: ip,
    cibleType: 'courrier',
    cibleId: data.courrierId,
    cibleLibelle: courrier.objet,
    description: `Vote "${data.statut}" sur le courrier "${courrier.objet}"`,
    details: data.commentaire ? { commentaire: data.commentaire } : undefined
  });
  
  // Log supplémentaire si le statut change
  if (nouveauStatut === 'valide') {
    await createLog({
      coproprieteId: courrier.coproprieteId,
      categorie: 'courrier',
      action: 'courrier_valide',
      niveau: 'important',
      acteurId: null,
      acteurNom: 'Système',
      cibleType: 'courrier',
      cibleId: data.courrierId,
      cibleLibelle: courrier.objet,
      description: niveau === 'rouge'
        ? `Courrier "${courrier.objet}" validé (1 approbation CS — niveau ROUGE)`
        : `Courrier "${courrier.objet}" validé à l'unanimité`
    });
  } else if (nouveauStatut === 'rejete') {
    await createLog({
      coproprieteId: courrier.coproprieteId,
      categorie: 'courrier',
      action: 'courrier_rejete',
      niveau: 'important',
      acteurId: null,
      acteurNom: 'Système',
      cibleType: 'courrier',
      cibleId: data.courrierId,
      cibleLibelle: courrier.objet,
      description: `Courrier "${courrier.objet}" rejeté`
    });
  }
}

// ============================================================================
// SESSION 92 : SURCLASSEMENT PAR LE COORDINATEUR
// ============================================================================

/**
 * Surclasse le niveau de classification d'un courrier
 * Le coordinateur peut monter (VERT→ORANGE, ORANGE→ROUGE) mais jamais descendre
 */
export async function surclasserNiveau(
  id: string,
  nouveauNiveau: NiveauClassification,
  userId: string,
  userNom: string
): Promise<void> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  if (courrier.statut !== 'brouillon') {
    throw new Error('Le niveau ne peut être modifié que sur un brouillon');
  }
  
  const niveauActuel = courrier.niveauClassification 
    || courrier.analyseSentinelle?.niveauClassification;
  
  if (!niveauActuel) {
    throw new Error('Aucun niveau de classification défini. Lancez d\'abord l\'analyse SENTINELLE.');
  }
  
  if (!peutSurclasser(niveauActuel, nouveauNiveau)) {
    throw new Error(`Impossible de passer de ${niveauActuel.toUpperCase()} à ${nouveauNiveau.toUpperCase()}. Le déclassement est interdit.`);
  }
  
  const now = Timestamp.now();
  const docRef = doc(db, COLLECTION_NAME, id);
  
  // Mettre à jour le niveau + le flag surclassement
  await updateDoc(docRef, {
    niveauClassification: nouveauNiveau,
    niveauSurclasse: true,
    updatedAt: now
  });
  
  // Mettre à jour aussi dans l'analyseSentinelle si elle existe
  if (courrier.analyseSentinelle) {
    await updateDoc(docRef, {
      'analyseSentinelle.niveauClassification': nouveauNiveau
    });
  }
  
  await createLog({
    coproprieteId: courrier.coproprieteId,
    categorie: 'courrier',
    action: 'courrier_modifie',
    niveau: 'important',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'courrier',
    cibleId: id,
    cibleLibelle: courrier.objet,
    description: `Courrier "${courrier.objet}" surclassé de ${niveauActuel.toUpperCase()} à ${nouveauNiveau.toUpperCase()}`,
    details: { 
      niveauPrecedent: niveauActuel, 
      nouveauNiveau,
      surclasseParCoordinateur: true 
    }
  });
}

// ============================================================================
// ANNULATION / DUPLICATION / ENVOI
// ============================================================================

/**
 * Annule un courrier (brouillon ou en_validation → annulé)
 */
export async function annulerCourrier(
  id: string,
  userId: string,
  userNom: string,
  motif?: string
): Promise<void> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  if (!['brouillon', 'en_validation'].includes(courrier.statut)) {
    throw new Error('Ce courrier ne peut plus être annulé');
  }
  
  const now = Timestamp.now();
  
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    statut: 'annule',
    updatedAt: now
  });
  
  await createLog({
    coproprieteId: courrier.coproprieteId,
    categorie: 'courrier',
    action: 'courrier_annule',
    niveau: 'important',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'courrier',
    cibleId: id,
    cibleLibelle: courrier.objet,
    description: `Courrier "${courrier.objet}" annulé`,
    details: motif ? { motif } : undefined
  });
}

/**
 * Duplique un courrier pour modification (après demande de modification)
 * Crée une nouvelle version avec le contenu de l'ancien
 * Session 92 : Conserve le niveauClassification du courrier parent
 */
export async function dupliquerCourrier(
  id: string,
  userId: string,
  userNom: string
): Promise<string> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  const now = Timestamp.now();
  const numero = await generateNumeroCourrier(courrier.coproprieteId);
  
  // Construire le nouveau courrier
  const newCourrierData: Record<string, unknown> = {
    coproprieteId: courrier.coproprieteId,
    numero,
    objet: courrier.objet,
    contenu: courrier.contenu,
    type: courrier.type,
    modeEnvoi: courrier.modeEnvoi,
    destinataire: courrier.destinataire,
    piecesJointes: courrier.piecesJointes,
    statut: 'brouillon',
    version: courrier.version + 1,
    courrierParentId: id,
    redacteurId: userId,
    redacteurNom: userNom,
    dateCreation: now,
    dateModification: now,
    validations: {},
    votesRequis: courrier.votesRequis,
    createdAt: now,
    updatedAt: now
  };
  
  // Ajouter les champs optionnels
  if (courrier.affaireId) {
    newCourrierData.affaireId = courrier.affaireId;
  }
  if (courrier.signalementIds && courrier.signalementIds.length > 0) {
    newCourrierData.signalementIds = courrier.signalementIds;
  }
  if (courrier.courrierReponseA) {
    newCourrierData.courrierReponseA = courrier.courrierReponseA;
  }
  
  // Session 92 : Conserver le niveau de classification
  if (courrier.niveauClassification) {
    newCourrierData.niveauClassification = courrier.niveauClassification;
    if (courrier.niveauSurclasse) {
      newCourrierData.niveauSurclasse = true;
    }
  }
  
  // Conserver l'analyse SENTINELLE pour référence
  if (courrier.analyseSentinelle) {
    newCourrierData.analyseSentinelle = courrier.analyseSentinelle;
  }
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), newCourrierData);
  
  await createLog({
    coproprieteId: courrier.coproprieteId,
    categorie: 'courrier',
    action: 'courrier_duplique',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'courrier',
    cibleId: docRef.id,
    cibleLibelle: courrier.objet,
    description: `Nouvelle version du courrier "${courrier.objet}" (v${courrier.version + 1})`,
    details: { courrierParentId: id, version: courrier.version + 1 }
  });
  
  return docRef.id;
}

/**
 * Marque un courrier comme envoyé
 */
export async function marquerCommeEnvoye(
  id: string,
  userId: string,
  userNom: string,
  modeEnvoi: string,
  gmailMessageId?: string,
  pdfUrl?: string
): Promise<void> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  if (courrier.statut !== 'valide') {
    throw new Error('Seuls les courriers validés peuvent être envoyés');
  }
  
  const now = Timestamp.now();
  
  const updateData: Record<string, unknown> = {
    statut: 'envoye',
    dateEnvoi: now,
    envoyePar: userId,
    modeEnvoi,
    updatedAt: now
  };
  
  if (gmailMessageId) {
    updateData.gmailMessageId = gmailMessageId;
  }
  
  if (pdfUrl) {
    updateData.pdfUrl = pdfUrl;
  }
  
  const docRef = doc(db, COLLECTION_NAME, id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, updateData as any);
  
  const actionLog = modeEnvoi === 'email'
    ? 'courrier_envoye_email'
    : modeEnvoi === 'recommande_ar'
      ? 'courrier_envoye_lrar'
      : 'courrier_envoye_postal';
  
  await createLog({
    coproprieteId: courrier.coproprieteId,
    categorie: 'courrier',
    action: actionLog as any,
    niveau: 'critique',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'courrier',
    cibleId: id,
    cibleLibelle: courrier.objet,
    description: `Courrier "${courrier.objet}" envoyé par ${modeEnvoi}`,
    details: {
      modeEnvoi,
      ...(gmailMessageId && { gmailMessageId }),
      ...(pdfUrl && { pdfUrl })
    }
  });
}

// ============================================================================
// SAUVEGARDE ANALYSE SENTINELLE
// ============================================================================

/**
 * Sauvegarde l'analyse SCUD d'un courrier
 * Session 92 : Sauvegarde aussi le niveauClassification sur le courrier
 */
export async function sauvegarderAnalyseSCUD(
  id: string,
  analyse: AnalyseSCUD
): Promise<void> {
  const courrier = await getCourrierById(id);
  if (!courrier) throw new Error('Courrier non trouvé');
  
  const docRef = doc(db, COLLECTION_NAME, id);
  
  const updateData: Record<string, unknown> = {
    analyseSentinelle: analyse,
    updatedAt: Timestamp.now()
  };
  
  // Session 92 : Stocker le niveauClassification directement sur le courrier
  if (analyse.niveauClassification) {
    updateData.niveauClassification = analyse.niveauClassification;
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, updateData as any);
}

// ============================================================================
// STATISTIQUES
// ============================================================================

/**
 * Compte les courriers par statut pour une copropriété
 */
export async function countCourriersByStatut(
  coproprieteId: string
): Promise<Record<StatutCourrier, number>> {
  const courriers = await getCourriersByCopropriete(coproprieteId);
  
  const counts: Record<StatutCourrier, number> = {
    brouillon: 0,
    en_validation: 0,
    valide: 0,
    envoye: 0,
    recu: 0,
    annule: 0,
    rejete: 0
  };
  
  for (const courrier of courriers) {
    counts[courrier.statut]++;
  }
  
  return counts;
}

/**
 * Récupère les courriers liés à une affaire
 */
export async function getCourriersByAffaire(affaireId: string): Promise<Courrier[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('affaireId', '==', affaireId),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({
    id: d.id,
    ...d.data()
  })) as Courrier[];
}

// Alias pour rétrocompatibilité
export const soumettreCourrier = soumettreCourrierValidation;
